import React, { useState, useEffect, useCallback } from 'react';
import socket from '../services/socket';
import { API_BASE_URL } from '../config';
import '../styles/AdminRecentRegistrations.css';

const getAdminToken = () => localStorage.getItem('adminKey') || '';

const AdminRecentRegistrations = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`);
      const data = await response.json();

      if (Array.isArray(data)) {
        // Sort by registered_at descending and take recent ones
        const sorted = data
          .filter(u => u.registered_at) // Only users with registration time
          .sort((a, b) => new Date(b.registered_at) - new Date(a.registered_at));
        setUsers(sorted);
        setError(null);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();

    // Refresh when new player registers
    const handlePlayerRegistered = () => {
      fetchUsers();
    };

    socket.on('player-registered', handlePlayerRegistered);

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchUsers, 30000);

    return () => {
      socket.off('player-registered', handlePlayerRegistered);
      clearInterval(interval);
    };
  }, [fetchUsers]);

  const openBadgePrint = (userId) => {
    window.open(`/badge-print/${userId}`, '_blank');
  };

  const openResetModal = (user) => {
    setResetModal(user);
    setNewPassword('');
    setResetMessage(null);
  };

  const closeResetModal = () => {
    setResetModal(null);
    setNewPassword('');
    setResetMessage(null);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      setResetMessage({ type: 'error', text: 'Password must be at least 4 characters' });
      return;
    }

    setResetting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${resetModal.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': getAdminToken()
        },
        body: JSON.stringify({ newPassword })
      });

      const data = await response.json();

      if (data.success) {
        setResetMessage({ type: 'success', text: `Password reset! New password: ${newPassword}` });
        setTimeout(() => {
          closeResetModal();
        }, 3000);
      } else {
        setResetMessage({ type: 'error', text: data.message || 'Failed to reset password' });
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setResetMessage({ type: 'error', text: 'Failed to connect to server' });
    } finally {
      setResetting(false);
    }
  };

  const generateSimplePassword = () => {
    // Generate a simple 4-digit password
    const password = String(Math.floor(1000 + Math.random() * 9000));
    setNewPassword(password);
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const then = new Date(dateString);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ago`;
  };

  if (loading) {
    return (
      <div className="recent-registrations">
        <div className="loading">Loading registrations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-registrations">
        <div className="error">{error}</div>
        <button onClick={fetchUsers} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="recent-registrations">
      <div className="section-header">
        <h3>Recent Registrations</h3>
        <div className="header-actions">
          <span className="user-count">{users.length} total</span>
          <button onClick={fetchUsers} className="refresh-btn">Refresh</button>
        </div>
      </div>

      <div className="registrations-list">
        {users.length === 0 ? (
          <div className="no-users">No registrations yet</div>
        ) : (
          users.slice(0, 20).map((user, index) => (
            <div
              key={user.id}
              className={`registration-item ${index < 3 ? 'recent' : ''}`}
            >
              <div className="user-info">
                <span className="user-number">#{users.length - index}</span>
                <div className="user-details">
                  <span className="username">
                    {user.username || user.short_id || 'Unnamed'}
                  </span>
                  <span className="user-id">{user.short_id}</span>
                </div>
              </div>

              <div className="registration-time">
                <span className="time">{formatTime(user.registered_at)}</span>
                <span className="time-ago">{getTimeSince(user.registered_at)}</span>
              </div>

              <div className="user-actions">
                <button
                  onClick={() => openBadgePrint(user.id)}
                  className="print-badge-btn"
                  title="Print badge"
                >
                  🖨️ Badge
                </button>
                <button
                  onClick={() => openResetModal(user)}
                  className="reset-password-btn"
                  title="Reset password"
                >
                  🔐 Reset
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {users.length > 20 && (
        <div className="show-more">
          Showing 20 of {users.length} registrations
        </div>
      )}

      {/* Password Reset Modal */}
      {resetModal && (
        <div className="reset-modal-overlay" onClick={closeResetModal}>
          <div className="reset-modal" onClick={e => e.stopPropagation()}>
            <div className="reset-modal-header">
              <h4>Reset Password</h4>
              <button onClick={closeResetModal} className="close-modal">×</button>
            </div>
            <div className="reset-modal-content">
              <p className="reset-user-info">
                User: <strong>{resetModal.username || resetModal.short_id}</strong>
              </p>

              {resetMessage && (
                <div className={`reset-message ${resetMessage.type}`}>
                  {resetMessage.text}
                </div>
              )}

              <div className="password-input-row">
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 4 chars)"
                  disabled={resetting}
                />
                <button
                  onClick={generateSimplePassword}
                  className="generate-btn"
                  disabled={resetting}
                  title="Generate simple password"
                >
                  🎲
                </button>
              </div>

              <div className="reset-modal-actions">
                <button onClick={closeResetModal} className="cancel-btn" disabled={resetting}>
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  className="confirm-reset-btn"
                  disabled={resetting || !newPassword}
                >
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecentRegistrations;
